import React, { useState, useEffect, useCallback } from 'react'
import { IonButton, IonAvatar, IonCard, IonCol, IonGrid, IonRow, IonSpinner, IonIcon, IonLabel, IonActionSheet, IonItemDivider, IonChip, IonSkeletonText } from '@ionic/react'
import { ProfileI } from "../interfaces/Profile";
import { MessageInterface } from "../interfaces/MessageInterface";
import { FriendInterface, FRIEND_STATUS } from '../interfaces/FriendInterface'
import { ConversationInterface } from '../interfaces/ConversationInterface'
import { archiveOutline, arrowForward, ellipse, ellipsisVertical, personAdd, timeOutline, trashBinOutline } from 'ionicons/icons'
import { getFriends, blockUnblockAction, isBlacklisted } from '../redux/actions/friendActions'
import { useSelector, useDispatch } from 'react-redux';
import { sendFriendRequest } from '../helpers/friend_actions'
import { openChat } from '../helpers/chat_actions';
import history from '../helpers/history';
import Api from "../services/Api";
import './MyMessages.scss'
import { RootState } from '../redux/reducers';
import { goToPage } from '../helpers/app_actions';
import { WalkI } from '../interfaces/Walk';
import dayjs from 'dayjs';
import { setUnreadAction } from '../redux/actions/messageAction';

interface ComponentState {
  person: ProfileI
  friends: Array<FriendInterface>
  conversations: Array<ConversationInterface>
  rooms: Array<any>
  showSend: boolean
  showMessages: boolean
  showCreateRoom: boolean
  showRoomMessages: boolean
  showRoomSend: boolean
  messages: Array<MessageInterface>
  chosenRoom: any
  chosenFriend: FriendInterface
  chosenFriendID: number
  text: string
  loaded: boolean
  error: string
}

const MyMessages = () => {
  const api = Api.getInstance();
  const dispatch = useDispatch();
  const friendsList = useSelector((state: RootState) => state.friends)
  const walks = useSelector((state: RootState) => state.walks.walks)
  const [showActionSheet, setShowActionSheet] = useState({
    open: false,
    personId: 0,
    personName: '',
    personPicture: ''
  });
  const [state, setState] = useState<ComponentState>({
      person: {} as ProfileI,
      friends: [],
      conversations: [],
      rooms: [],
      messages: [],
      loaded: false,
      showSend: false,
      showMessages: false,
      showCreateRoom: false,
      showRoomMessages: false,
      showRoomSend: false,
      chosenRoom: {},
      chosenFriend: {} as FriendInterface,
      chosenFriendID: 0,
      text: '',
      error: ''
  })
  const regex = /([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]/g

  const getData = useCallback(() => {
    api.get('conversations').then(result => {
      result.json().then(resultJson => {
        if (resultJson.status === 'ok') {
          setState({
            ...state,
            person: resultJson.person,
            conversations: resultJson.conversations,
            loaded: true
          });

          if(resultJson.conversations.find((o: any) => o.unreadMessages === true)) {
            dispatch(setUnreadAction(true))
          } else {
            dispatch(setUnreadAction(false))
          }
        }
      });
    });
  }, [api, state])

  useEffect(() => {
    dispatch(getFriends())
    const willFocusSubscription = history.listen(() => {
      dispatch(getFriends())
      getData()
    })

    return willFocusSubscription;
  }, [dispatch, getData]);

  useEffect(() => {
    if(!state.loaded) {
      getData()
    }
  }, [getData, state.loaded])

  const answerRequest = (friend: FriendInterface, status: FRIEND_STATUS) => {
    api.get('friend_reply_request/' + friend.friendId + '/' + status).then(result => {
      result.json().then(resultJson => {
        if (resultJson.status === 'ok') {
         dispatch(getFriends())
        }
      });
    });
  }

  const isArchived = (friendId: number) => {
    for (const archived of friendsList.archived) {
      if (archived.id === friendId) {
        return true
      }
    }
    return false
  }

  const toggleArchived = (friendId: number) => {
    const archived = isArchived(friendId)
    const doArchive = archived ? 0 : 1
    api.get('friend_archive/' + friendId + '/' + doArchive).then(result => {
      result.json().then(resultJson => {
        if (resultJson.status === 'ok') {
          getData()
          dispatch(getFriends())
        }
      });
    });
  }

  const isFriend = (friendId: number) => {
    for (const friend of friendsList.friends) {
      if (friend.friendId === friendId) {
        return true
      }
    }
    return false
  }

  const buttonArray = [{
    text: api.tr('block') + showActionSheet.personName,
    role: 'destructive',
    icon: trashBinOutline,
    handler: () => {
      dispatch(blockUnblockAction(showActionSheet.personId, friendsList.blacklist))
      dispatch(getFriends())
    }
  },{
    text: 'Öppna chatten',
    role: '',
    icon: arrowForward,
    handler: () => {
      setShowActionSheet({
        ...showActionSheet,
        open: false
      })
      goToPage('/walk/chat/'+showActionSheet.personId)
    }
  }
]

if(!isArchived(showActionSheet.personId)) {
  buttonArray.unshift({
    text: api.tr('archive_chat_with') + showActionSheet.personName,
    role: '',
    icon: archiveOutline,
    handler: () => {
      toggleArchived(showActionSheet.personId)
    }
  })
}

if(isArchived(showActionSheet.personId)) {
  buttonArray.unshift({
    text: api.tr('activate_chat_with') + showActionSheet.personName,
    role: '',
    icon: archiveOutline,
    handler: () => {
      toggleArchived(showActionSheet.personId)
    }
  })
}

if(!isFriend(showActionSheet.personId)) {
  buttonArray.unshift({
    text: api.tr('send_friendrequest_to') + showActionSheet.personName,
    role: 'send',
    icon: personAdd,
    handler: () => {
      sendFriendRequest(showActionSheet.personId)
    }
  })
} 

const isInThePast = (date: Date) => {
  const today = new Date()
  const toDate = new Date(date)

  return toDate < today;
}

const findClosestWalkWithPerson = (walks: Array<WalkI>) => {
  let closestWalk: JSX.Element | null = null
  const today = dayjs().hour(0).minute(0).second(1)
  const upcoming = walks.filter((walk: WalkI) => !isInThePast(dayjs(walk.start).toDate()) ? walk : null)
  const past = walks.filter((walk: WalkI) => isInThePast(dayjs(walk.start).toDate()) ? walk : null)
  const tid = () => {
    const walkStart = dayjs(sortByDate(upcoming)[0].start).hour(0).minute(0).second(2)
    if(walkStart.diff(today, 'day') === 0) {
      return api.tr('walkInfo_today') + dayjs(sortByDate(upcoming)[0].start).format('HH:mm')
    } else if(today.diff(dayjs(sortByDate(upcoming)[0].start), 'day') === 1){
      return api.tr('walkInfo_tomorrow') + dayjs(sortByDate(upcoming)[0].start).format('HH:mm')
    } else if(today.diff(dayjs(sortByDate(upcoming)[0].start), 'day') === 2){
      return api.tr('walkInfo_twodays') + dayjs(sortByDate(upcoming)[0].start).format('HH:mm')
    } else {
      return api.tr('walkInfo_in_days') + Math.abs(today.diff(dayjs(sortByDate(upcoming)[0].start), 'day')) + api.tr('walkInfo_day')
    }
  }

  if(sortByDate(upcoming)[0]) {
    closestWalk = <IonChip className="background_teal p-2 m-2">{tid()}</IonChip>
  } else if(sortByDate(past)[0]){
    closestWalk = <IonChip color="teal" className="p-2 m-2">{api.tr('walkInfo_latest') + dayjs(sortByDate(past)[0].start).format('YYYY-MM-DD')}</IonChip>
  } else {
    closestWalk = null
  }
  return closestWalk
}

const sortByDate = (array: Array<WalkI>) => {
  return array.sort((a, b) => {
    return new Date(b.start).getTime() - new Date(a.start).getTime()
  })
}

  return (
    <section 
      className="m-2 myPage__content safe_padding_bottom">  
        { state.loaded ? 
        <IonGrid>
      {
        friendsList.friendRequests.map((request: any, key: number) => (
          <IonRow key={request.id + key}>
            <IonCard class="mt-2 p-2 fullWidth flex">
              <IonCol size="3">
                <IonAvatar >
                  <img src={request.picture} alt=""/>
                </IonAvatar>
              </IonCol>
              <IonCol size="9">
                <IonRow>
                  <p>{request.firstName} {api.tr('has_sent_friendrequest')}</p>
                </IonRow>
                <IonRow>
                  <IonButton 
                    onClick={() => answerRequest(request, FRIEND_STATUS.ACCEPTED)}
                    class="modalButton--end">{api.tr('accept')}</IonButton>
                  <IonButton 
                    onClick={() => answerRequest(request, FRIEND_STATUS.DENIED_BY_FRIEND)}
                    class="modalButton--end" color="danger">{api.tr('deny')}</IonButton>
                </IonRow>
              </IonCol>
            </IonCard>
          </IonRow>
        ))
      }    
    <IonItemDivider color="darkblue" class="p-2 mt-2">
      <IonLabel>{api.tr('active_conversations')}</IonLabel>
    </IonItemDivider>
    { state.conversations.map((person, key) => {
      return <section key={key}>
          <IonRow key={key} onClick={() => {
            openChat(person.friendId)
          }}>
          <IonCard class="mt-2 p-2 fullWidth flex flex-col">
            <IonRow>
            <IonCol size="3">
              <IonAvatar class="button_shadow">
                <img src={person.picture} alt=""/>
              </IonAvatar>
            </IonCol>
            <IonCol size="7">
              <IonRow>
                <IonCol class="flex justify-between items-center">
                  <IonCol class="flex items-center">
                    <IonLabel><b>{person.firstName} </b></IonLabel>
                    <IonLabel className="flex items-center">
                      <IonIcon icon={ellipse} style={{fontSize: '8px'}} className="m-2"/>
                      {person.createdAt.match(regex)}
                    </IonLabel>
                    { person.unreadMessages ? 
                      <IonIcon icon={ellipse} color="primary" className="ml-2"/>
                    : null }
                  </IonCol>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="9" class="mt-1">
                  { person.lastMessage.messageType === 0 ? 
                    <p className="ellipsis">{person.friendId === person.lastMessage.recipientId ? api.tr('whoWhen') : null} {person.lastMessage.text}</p>
                    : 
                    <>
                      {
                        person.lastMessage.messageType === 1 ?
                        <>
                          <IonLabel>{person.friendId === person.lastMessage.recipientId ? api.tr('whoWhen') : null }</IonLabel>
                          <IonLabel class="color_green"> {api.tr('walk_suggestion')}</IonLabel>
                        </>
                        :
                        <>
                        {
                          person.lastMessage.messageType === 3 ?
                          <>
                            { person.lastMessage.text === 'Promenaden har bokats' || person.lastMessage.text === 'The walk was booked' ? 
                              <p>{api.tr('booked_confirmation')}</p>
                              : 
                              <p>{api.tr('cancel_confirmation')}</p>
                            }
                          </>
                          : null
                        }
                        </>
                      }
                    </>
                    }
                </IonCol>
              </IonRow>

            </IonCol>
            <IonCol 
              onClick={(e) => {
                setShowActionSheet({
                  open: true,
                  personId: person.friendId,
                  personName: person.firstName,
                  personPicture: person.picture
                })
                e.stopPropagation()
              }}
              class="flex justify-end items-center"
              size="2">
              <IonIcon 
                icon={ellipsisVertical} 
                size="large"/>
            </IonCol>
            </IonRow>
            <IonRow className="fullWidth justify-start mt-2">
              {findClosestWalkWithPerson(walks.filter((walk: WalkI) => walk.personId === person.friendId || walk.bookedPerson?.id === person.friendId))}
            </IonRow>
          </IonCard>
        </IonRow>
        <IonActionSheet
          isOpen={showActionSheet.open}
          onDidDismiss={() => setShowActionSheet({
            ...showActionSheet,
            open: false
          })}
          cssClass='my-custom-class'
          buttons={buttonArray}/>
      </section> 
    }) 
    }
      <IonItemDivider color="medium" class="p-2 mt-2">
        <IonLabel>{api.tr('archived_conversations')}</IonLabel>
      </IonItemDivider>
      <IonRow>
        {friendsList.archived.map((archived: any, key: number) => {
          return <section className="fullWidth" key={key}> {
            !isBlacklisted(archived.id, friendsList.blacklist) ?
            <IonCard key={key} onClick={() => {
            setShowActionSheet({
              open: true,
              personId: archived.id,
              personName: archived.firstName,
              personPicture: archived.picture
            })
          }} class="mt-2 p-2 fullWidth flex">
          <IonCol size="3">
            <IonAvatar class="button_shadow">
              <img src={archived.picture} alt=""/>
            </IonAvatar>
          </IonCol>
          <IonCol size="7">
            <IonRow>
              <IonCol class="flex justify-between items-center">
                <IonCol class="flex items-center">
                  <IonLabel><b className="color_grey">{archived.firstName} </b></IonLabel>
                  <IonLabel className="ml-2 flex items-center">
                    <IonIcon color="medium" icon={timeOutline} />
                    {/* {archived.createdAt.match(regex)} */}
                  </IonLabel>
                </IonCol>
                {/* { archived.unreadMessages ? 
                  <IonIcon icon={ellipse} color="primary" class="ml-2"/>
                : null } */}
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol size="9">
                <p className="color_grey">{api.tr('conversation_archived')}</p>
              </IonCol>
            </IonRow>
          </IonCol>
          <IonCol 
            onClick={(e) => {
              setShowActionSheet({
                open: true,
                personId: archived.id,
                personName: archived.firstName,
                personPicture: archived.picture
              })
              e.stopPropagation()
            }}
            class="flex justify-end items-center"
            size="2">
            <IonIcon 
              icon={ellipsisVertical} 
              size="large"/>
          </IonCol>
        </IonCard>
        : null
        }
        </section>
        })}
        <IonActionSheet
          isOpen={showActionSheet.open}
          onDidDismiss={() => setShowActionSheet({
            ...showActionSheet,
            open: false
          })}
          cssClass='my-custom-class'
          buttons={buttonArray}/>
      </IonRow>
    </IonGrid>
    :
    <IonRow>
      <IonSpinner style={{margin: '2rem auto'}} name="lines"/>
    </IonRow>
    }
    </section>
  )
}

export default MyMessages;